import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { HrailScrew, InsertHrailScrewBody, UpdateHrailScrewBody } from "./types/screws";

export const useHrailScrewsApi = () => {
  const api = useApi();

  const getAdminHrailScrew = useCallback(
    (screwId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/screws/${screwId}`;
      return useSWRMutation<HrailScrew, CustomError, string>(url, (url) =>
        api.get<HrailScrew>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailScrews = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/screws`, query);
      return useSWRImmutable<Paginated<HrailScrew>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailScrew>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailScrews = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/screws`, query);
      return api.get<Paginated<HrailScrew>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailScrew = useCallback(
    async (body: InsertHrailScrewBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailScrewBody>(`/admin/settings/hrail/screws`, body);
      mutateCache([`/admin/settings/hrail/screws`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailScrew = useCallback(
    async (screwId: string, body: UpdateHrailScrewBody): Promise<void> => {
      const url = `/admin/settings/hrail/screws/${screwId}`;
      await api.put<UpdateHrailScrewBody>(url, body);
      mutateCache([`/admin/settings/hrail/screws`], url);
    },
    [api],
  );

  const updateAdminHrailScrewVisibility = useCallback(
    async (screwId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/screws/${screwId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/screws`]);
    },
    [api],
  );

  const updateAdminHrailScrewOrder = useCallback(
    async (screwId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/screws/${screwId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/screws`]);
    },
    [api],
  );

  const deleteAdminHrailScrew = useCallback(
    async (screwId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/screws/${screwId}`);
      mutateCache([`/admin/settings/hrail/screws`]);
    },
    [api],
  );

  return {
    getAdminHrailScrew,
    useAdminHrailScrews,
    getAdminHrailScrews,
    insertAdminHrailScrew,
    updateAdminHrailScrew,
    updateAdminHrailScrewVisibility,
    updateAdminHrailScrewOrder,
    deleteAdminHrailScrew,
  };
};
